import { PhotoMetadataRecord, PhotoRecord } from "@/lib/catalog"
import { PhotoId } from "@/store/undoable-state"
import { getCameraModel } from "@/store/processing/image-info"
import { didFlashFire } from "@/utils/exif"

interface PhotoMetadata {
	photo: PhotoRecord
	lastModified: number
	ISO: number
	time: number
	camera: string
	flash: boolean
}

export const getCalibrationImages2D = (
	photos: PhotoRecord[],
	allMetadata: Record<PhotoId, PhotoMetadataRecord>,
) => {
	const infoListAll: PhotoMetadata[] = []

	let minMinutes: number | null = null
	let maxMinutes: number | null = null

	const sampleEvery: number = Math.max(1, Math.round(photos.length / 200))

	const sampledPhotos = photos.filter((_, index) => index % sampleEvery === 0)
	const cameraCount: Record<string, number> = {}

	for (let i = 0; i < sampledPhotos.length; i++) {
		const photo = sampledPhotos[i]

		if (!photo) {
			continue
		}

		const metadata = allMetadata[photo.id]

		if (!metadata || !metadata.isComplete) {
			continue
		}

		const date = new Date(metadata.dateCreated ?? metadata.dateModified)

		const minute = date.getDay() * 24 * 60 + date.getHours() * 60 + date.getMinutes()

		if (!minMinutes || minute < minMinutes) {
			minMinutes = minute
		}
		if (!maxMinutes || minute > maxMinutes) {
			maxMinutes = minute
		}

		const camera = getCameraModel(metadata)
		cameraCount[camera] = (cameraCount[camera] ?? 0) + 1

		infoListAll.push({
			photo: photo,
			lastModified: metadata.dateModified,
			ISO: metadata.iso ?? 0,
			time: minute,
			camera: camera,
			flash: didFlashFire(metadata.flash),
		})
	}

	if (!minMinutes || !maxMinutes) {
		return { finalPhotos: [], recommendedCount: 0 }
	}

	const recommendedCount = Math.max(12, Math.min(20, Math.round(photos.length / 50)))
	const minCountAll = Math.round(Math.max(2, recommendedCount / 2, photos.length / 100))

	let interval = (maxMinutes - minMinutes) / (recommendedCount * 4)
	interval = Math.round(Math.min(Math.max(5, interval), 15))

	const isoInterval = 100

	const totalCount = infoListAll.length
	const selected_photos: PhotoMetadata[] = []
	const sortedCameraCount = Object.entries(cameraCount).sort((a, b) => b[1] - a[1])
	for (const entry of sortedCameraCount) {
		const camera = entry[0]
		const infoList = infoListAll.filter((data) => data.camera === camera)

		const ratio = infoList.length / totalCount
		const recommendedCountForCamera = Math.max(1, Math.round(recommendedCount * ratio))
		const minCount = Math.max(1, Math.round(minCountAll * ratio))

		console.log(
			"getCalibrationImages2D:",
			"Number of photos and recommendedCount for camera:",
			camera,
			infoList.length,
			recommendedCountForCamera,
		)

		const histogram: { [key: string]: number } = {} // 2D histogram for time and iso

		infoList.forEach((data) => {
			const minute = Math.floor((data.time - (minMinutes || 0)) / interval) * interval
			data.time = minute
			let iso = Math.floor(data.ISO / isoInterval) * isoInterval
			if (data.flash) iso = -10000 // add a special iso for flash
			data.ISO = iso
			const key = `${minute},${iso}`
			histogram[key] = (histogram[key] || 0) + 1
		})

		const sortedHistogram = Object.entries(histogram).sort((a, b) => b[1] - a[1])
		console.log("getCalibrationImages2D:", { sortedHistogram })

		// NMS to pick topK photos
		const pickedMinuteISOs = sortedHistogram[0]
			? [sortedHistogram[0][0].split(",").map((x) => parseInt(x, 10))]
			: [] // [[minute, iso]]

		let i = 1
		let singletonCount = 0
		while (pickedMinuteISOs.length < recommendedCountForCamera && i < sortedHistogram.length) {
			const histogramEntry = sortedHistogram[i]

			if (!histogramEntry) continue

			if (singletonCount >= 2 && pickedMinuteISOs.length >= minCount) {
				break
			}

			const histogramEntryValues = histogramEntry[0].split(",").map((x) => parseInt(x, 10))

			const minute = histogramEntryValues[0] ?? 0
			const iso = histogramEntryValues[1] ?? 0

			const diffMinute = pickedMinuteISOs.map((prev) => Math.abs((prev[0] || 0) - minute))
			const diffISO = pickedMinuteISOs.map(
				(prev) => Math.abs((prev[1] || 0) - iso) / Math.max(prev[1] || 0, iso),
			)
			const diffAll = diffMinute.map(
				(item, index) => item / (1.9 * interval) + (diffISO[index] || 0) * 2,
			)
			if (Math.min(...diffAll) > 1) {
				pickedMinuteISOs.push([minute, iso])
				if (histogramEntry[1] <= 1) singletonCount++
			}
			i += 1
		}

		console.log(
			"getCalibrationImages2D:",
			`pickedMinuteISOs ${pickedMinuteISOs}; recommendedCount ${pickedMinuteISOs.length}`,
		)

		for (const [minute, iso] of pickedMinuteISOs) {
			// Filter the list to only include photos taken in this minute
			const photos = infoList.filter((data) => data.time == minute && data.ISO == iso)

			// Select a random photo from this list
			const photo = photos[Math.floor(Math.random() * photos.length)]

			if (photo) {
				selected_photos.push(photo)
			}
		}
	}

	console.log(`final selected_photos:`, { selected_photos })
	const topPhoto = selected_photos[0]?.photo

	const finalPhotos = selected_photos
		.sort((a, b) => {
			return a.lastModified - b.lastModified
		})
		.map((metadata: PhotoMetadata) => metadata.photo)

	console.log({ finalPhotos })

	return { finalPhotos, topPhoto }
}
